import { useFetch } from "../../../hooks/api";
import { useMemo } from "react";
import { Row, Col, Table } from "react-bootstrap";
import Loading from "../../../components/core/blocks/Loading";
import { formatCurrency, formatPercent, formatNumber } from "../../../utils/formatNumber";
import moment from "moment";
import { keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "../../../i18n/useDateFormatter";

const ImpactSection = ({ pricingProduct, marketPlace }) => {
  const { t } = useTranslation(["pricingv2"]);
  const longDate = useDateFormatter("long");
  const now = moment(moment(new Date()).format("YYYY-MM-DD")).subtract(1, "days");
  const start = moment(moment(pricingProduct.enroll_date).format("YYYY-MM-DD"));
  const end = now;
  const preEnd = start.clone().subtract(1, "days");
  const preStart = preEnd.clone().subtract(end.diff(start, "days"), "days");

  const filters = {
    mp: marketPlace.marketPlace,
    date_group: "day",
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    group_by: "product_id",
  };

  const { data, isLoading } = useFetch(
    ["product", marketPlace.marketPlace, "impact"],
    `/api/data_report/product_report?product_id=${pricingProduct.product.id}`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const { currentAvgPrice, previousAvgPrice } = useMemo(() => {
    if (!data) {
      return {
        currentAvgPrice: 0,
        previousAvgPrice: 0,
      };
    }

    let totalPricesPrev = 0;
    let totalPrices = 0;
    let priceCount = 0;
    for (let i = 0; i < data.date.length; i++) {
      if (data.date[i].product_price && data.date[i].product_price_prev) {
        totalPrices += data.date[i].product_price;
        totalPricesPrev += data.date[i].product_price_prev;
        priceCount++;
      }
    }

    return {
      currentAvgPrice: totalPrices / priceCount,
      previousAvgPrice: totalPricesPrev / priceCount,
    };
  }, [data]);

  if (isLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <Row className="p-3">
      <Col xs={12} className="py-5">
        <Table
          borderless
          hover
          size="sm"
          className="fs-4"
          style={{
            minWidth: "900px",
            maxWidth: "1600px",
            margin: "0 auto",
          }}
        >
          <thead>
            <tr>
              <th className="text-start" style={{ width: "150px" }}>
                {t(($) => $.pricingv2["Period"])}
              </th>
              <th className="text-start">{t(($) => $.pricingv2["Range"])}</th>
              <th className="text-end">{t(($) => $.pricingv2["Avg Price"])}</th>
              <th className="text-end">{t(($) => $.pricingv2["Units"])}</th>
              <th className="text-end">{t(($) => $.pricingv2["Sales"])}</th>
              <th className="text-end">{t(($) => $.pricingv2["Profit"])}</th>
              <th className="text-end">{t(($) => $.pricingv2["Margin"])}</th>
              <th className="text-end">{t(($) => $.pricingv2["BSR"])}</th>
            </tr>
          </thead>
          {data.products.length > 0 && (
            <tbody>
              {/* Current */}
              <tr>
                <td>
                  <strong>{t(($) => $.pricingv2["Current"])}</strong>
                </td>
                <td style={{ maxWidth: "150px" }}>
                  {t(($) => $.pricingv2["{start} to {end}"], {
                    start: longDate.format(moment(start).toDate()),
                    end: longDate.format(moment(end).toDate()),
                  })}
                </td>
                <td className="text-end">
                  {formatCurrency(currentAvgPrice, {
                    marketPlace: marketPlace.marketPlace,
                  })}
                </td>
                <td className="text-end">{data.products[0].total_units}</td>
                <td className="text-end">
                  {formatCurrency(data.products[0].total_sales, {
                    marketPlace: marketPlace.marketPlace,
                    decimalPlaces: 0,
                  })}
                </td>
                <td className="text-end">
                  {formatCurrency(data.products[0].profit, {
                    marketPlace: marketPlace.marketPlace,
                    decimalPlaces: 0,
                  })}
                </td>
                <td className="text-end">
                  {formatPercent(data.products[0].margin, {
                    decimalPlaces: 0,
                  })}
                </td>
                <td className="text-end">{formatNumber(data.products[0].bsr_rank, { decimalPlaces: 0 })}</td>
              </tr>

              {/* Previous */}
              <tr style={{ borderBottom: "2px solid #000" }}>
                <td>
                  <strong>{t(($) => $.pricingv2["Previous"])}</strong>
                </td>
                <td style={{ maxWidth: "150px" }}>
                  {t(($) => $.pricingv2["{start} to {end}"], {
                    start: longDate.format(moment(preStart).toDate()),
                    end: longDate.format(moment(preEnd).toDate()),
                  })}
                </td>
                <td className="text-end">
                  {formatCurrency(previousAvgPrice, {
                    marketPlace: marketPlace.marketPlace,
                  })}
                </td>
                <td className="text-end">{data.products[0].total_units_prev}</td>
                <td className="text-end">
                  {formatCurrency(data.products[0].total_sales_prev, {
                    marketPlace: marketPlace.marketPlace,
                    decimalPlaces: 0,
                  })}
                </td>
                <td className="text-end">
                  {formatCurrency(data.products[0].profit_prev, {
                    marketPlace: marketPlace.marketPlace,
                    decimalPlaces: 0,
                  })}
                </td>
                <td className="text-end">
                  {formatPercent(data.products[0].margin_prev, {
                    decimalPlaces: 0,
                  })}
                </td>
                <td className="text-end">{formatNumber(data.products[0].bsr_rank_prev, { decimalPlaces: 0 })}</td>
              </tr>

              {/* Delta */}
              <tr>
                <td>
                  <strong>{t(($) => $.pricingv2["Lifetime Impact"])}</strong>
                </td>
                <td style={{ maxWidth: "150px" }}>&nbsp;</td>
                <td className="text-end">
                  {formatCurrency(currentAvgPrice - previousAvgPrice, {
                    marketPlace: marketPlace.marketPlace,
                    addPlus: true,
                  })}
                </td>
                <td className="text-end">
                  {formatNumber(data.products[0].total_units_delta, {
                    addPlus: true,
                  })}
                </td>
                <td className="text-end">
                  {formatCurrency(data.products[0].total_sales_delta, {
                    marketPlace: marketPlace.marketPlace,
                    decimalPlaces: 0,
                    addPlus: true,
                  })}
                </td>
                <td className="text-end">
                  {formatCurrency(data.products[0].profit_delta, {
                    marketPlace: marketPlace.marketPlace,
                    decimalPlaces: 0,
                    addPlus: true,
                  })}
                </td>
                <td className="text-end">
                  {formatPercent(data.products[0].margin_delta, {
                    decimalPlaces: 0,
                  })}
                </td>
                <td className="text-end">
                  {formatNumber(data.products[0].bsr_rank_delta, {
                    addPlus: true,
                    decimalPlaces: 0,
                  })}
                </td>
              </tr>
            </tbody>
          )}

          {data.products.length === 0 && (
            <tbody>
              {/* Current */}
              <tr>
                <td colSpan="8" className="py-5 text-center">
                  <strong>{t(($) => $.pricingv2["Dynamic Pricing Impact will be available tomorrow"])}</strong>
                </td>
              </tr>
            </tbody>
          )}
        </Table>
      </Col>
    </Row>
  );
};

export default ImpactSection;
